<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="depositBalanceReportTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="title"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithCustomTime(data.value) : '' }}
      </template>

      <template #cell(expiresAt)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(url)="data">
        <Button
          :label="$t('Download')"
          size="md"
          icon="DownloadIcon"
          show-icon
          class="mr-1"
          @click="downloadFile(data)"
        >
          <DownloadIcon class="mr-2" />
          Download
        </Button>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="downloadHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Title -->
            <b-col md="6">
              <InputField
                id="title"
                v-model="filter.title"
                :label="$t('Title')"
                :placeholder="$t('Enter Title')"
                type="text"
                for="title"
              />
            </b-col>
            <!-- Start Date From -->
            <b-col md="6">
              <DatePickerField
                id="start-date From"
                v-model="filter.startDate"
                :min-date="new Date()"
                :label="$t('Start Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Start Date From')"
              />
            </b-col>
            <b-col md="6" />
            <b-col md="6">
              <InputField
                id="initiated-by"
                v-model="filter.initiatedBy"
                :label="$t('initiatedBy')"
                :placeholder="$t('Enter initiatedBy')"
                type="text"
                for="initiated-by"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import { toCustomDate, toDateWithYear, toDateWithCustomTime } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    BModal,
    BForm,
    TableEntries,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabled: false,
      toDateWithYear,
      toDateWithCustomTime,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Customer Account Balance Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'title', sortable: true, label: 'Title' },
        { key: 'initiatedBy', sortable: true, label: 'Initiated By' },
        { key: 'expiresAt', sortable: true, label: 'Expires At' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'url', label: 'URL' },
      ],
      filter: {
        title: '',
        financialDateFrom: null,
        financialDateTo: null,
        productCode: '',
        branchCode: '',
        customerType: '',
        taxType: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedCustomerTypeOptions() {
      return this.customerTypeOptions.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.value,
      }))
    },
    translatedTaxTypeOptions() {
      return this.taxTypeOptions.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.value,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchDownloadHistoryReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchDownloadHistoryReport()
    },
  },
  mounted() {
    this.fetchDownloadHistoryReport()
  },
  methods: {
    ...mapActions({
      getDownlaodHistoryReport: 'reports/getDownlaodHistoryReport',
    }),
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.fetchDownloadHistoryReport()
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.downloadHistoryRules.validate().then(async success => {
          if (success) {
            this.fetchDownloadHistoryReport()
          }
        })
      } else {
        this.fetchDownloadHistoryReport()
      }
    },
    async fetchDownloadHistoryReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getDownlaodHistoryReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.VIEW_REPORT_DOWNLOADS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async downloadFile(data) {
      const url = new URL(data.item.url)
      const documentId = url.searchParams.get('DocumentId')
      // Extract file extension
      const extension = documentId.split('.').pop()
      const link = document.createElement('a')
      link.href = url
      link.download = `${data.item.title}.${extension}`
      link.target = '_blank'
      link.click()
    },
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
